import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// Resolves whether the given user is staff (admin) via the is_admin() RPC.
//
// Caller passes `userId` rather than letting this hook call `useAuth()`
// itself — otherwise the internal useAuth instance races against the
// caller's useAuth instance during initial hydration: AdminGate's own
// useAuth resolves first, this hook's lags one tick behind, the query
// stays disabled (userId still null), `data` defaults to false, and
// AdminGate redirects to "/" before the RPC ever fires.
//
// Used by AdminGate to gate /admin/* route mounts. UX gate only — server
// is authoritative via is_admin() in RLS / RPC predicates.
export function useIsStaff(userId: string | null) {
  const query = useQuery({
    queryKey: ["is-staff", userId],
    queryFn: async () => {
      if (!userId) return false;
      const { data, error } = await supabase.rpc("is_admin", {
        p_user_id: userId,
      });
      if (error) throw error;
      return data === true;
    },
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });

  return {
    data: query.data ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    refetch: query.refetch,
  };
}
